import * as turf from '@turf/turf'
import type GeoJSON from 'geojson'
import type {
  FeatureCollection,
  Feature as GeoFeature,
  Polygon as GeoPolygon,
  MultiPolygon,
  Position
} from 'geojson'
import { isNil, isNonNil } from 'utils/object'

import { Line, Ring } from './point-and-line-operations'
import type { LineLikeFeature, PolyLikeFeature, PolyLikeGeometry } from './types'

export namespace Feature {
  /**
   * Checks if a polygon or multipolygon feature is self-intersecting, i.e., if one or more regions of the polygon
   * (or its holes) overlap with themselves.
   *
   * If it is, it returns a union of the feature with itself, which can be used to visualize the self-intersection.
   * If not, it returns false.
   */
  export const normalize = (feature: GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>) => {
    const union = turf.union(turf.featureCollection([feature, feature]))

    if (isNil(union)) {
      return feature
    }

    try {
      if (turf.booleanEqual(feature, union)) return feature
    } catch (e) {
      console.error(feature, e)
      return feature
    }

    union.id = feature.id // ensure the union has the same id as the original feature
    union.properties = feature.properties

    return union
  }

  /**
   * Flattens a collection of features into individual polygon features.
   */
  export const flatten = (
    features: (PolyLikeFeature | LineLikeFeature)[] | PolyLikeFeature | LineLikeFeature
  ): (GeoJSON.Feature<GeoJSON.Polygon> | GeoJSON.Feature<GeoJSON.LineString>)[] => {
    if (!Array.isArray(features)) {
      features = flatten([features])
    }
    return features.flatMap((feature) => {
      if (feature.geometry.type === 'Polygon') {
        const feat = Feature.normalize(feature as GeoJSON.Feature<GeoJSON.Polygon>)
        feat.id = feature.id ?? crypto.randomUUID()
        feat.properties = feature.properties
        return feat as GeoJSON.Feature<GeoJSON.Polygon>
      }
      if (feature.geometry.type === 'MultiPolygon') {
        return turf.flatten(feature).features.map((poly) => {
          poly.id = crypto.randomUUID()
          poly.properties = feature.properties
          return Feature.normalize(poly) as GeoJSON.Feature<GeoJSON.Polygon>
        })
      }
      if (feature.geometry.type === 'LineString') {
        const feat = feature
        feat.id = feature.id ?? crypto.randomUUID()
        feat.properties = feature.properties
        return feat as GeoJSON.Feature<GeoJSON.LineString>
      }
      return []
    })
  }
}

export namespace Polygon {
  /**
   * Flattens a collection of polygon and multipolygon features into individual polygon features.
   */
  export const flatten = (
    features: PolyLikeFeature[] | PolyLikeFeature
  ): GeoJSON.Feature<GeoJSON.Polygon>[] => {
    if (!Array.isArray(features)) {
      features = flatten([features])
    }
    return features.flatMap((feature) => {
      if (feature.geometry.type === 'Polygon') {
        const feat = Feature.normalize(feature)
        feat.id = feature.id ?? crypto.randomUUID()
        feat.properties = feature.properties
        return feat as GeoJSON.Feature<GeoJSON.Polygon>
      }
      if (feature.geometry.type === 'MultiPolygon') {
        return turf.flatten(feature).features.map((poly) => {
          poly.id = crypto.randomUUID()
          poly.properties = feature.properties
          return Feature.normalize(poly) as GeoJSON.Feature<GeoJSON.Polygon>
        })
      }
      return []
    })
  }

  /**
   * Adds vertices to the given polygon where it touches another polygon's vertices.
   *
   * If we don't do this, performing a union on polygons can result in degenerate cases,
   * see https://imgur.com/a/yFs5m4J for an example, which is also reproduced in the
   * unit tests ("should avoid leaving edges behind due to missing shared vertices").
   *
   * Essentially, for each polygon, we check if any vertex from the other polygons
   * lies on its edges. If so, we split that edge at the vertex point.
   * ```
   */
  export const ensureSharedVertices = (
    polygon: GeoJSON.Feature<GeoJSON.Polygon>,
    others: GeoJSON.Feature<GeoJSON.Polygon>[]
  ): [GeoJSON.Feature<GeoJSON.Polygon>, GeoJSON.Feature<GeoJSON.Polygon>[]] => {
    if (others.length === 0) {
      return [polygon, others]
    }

    const clonedPolygon = turf.clone(polygon)
    const clonedOthers = others.map((o) => turf.clone(o))

    // Capture original rings before any modifications
    const originalPolygonRings = clonedPolygon.geometry.coordinates
    const originalOtherRings = clonedOthers.map((o) => o.geometry.coordinates)

    // Insert polygon's original vertices into others
    for (let i = 0; i < clonedOthers.length; i++) {
      for (const ring of originalPolygonRings) {
        for (let v = 0; v < ring.length - 1; v++) {
          clonedOthers[i].geometry.coordinates = Line.insertVertex(
            ring[v],
            clonedOthers[i].geometry.coordinates
          )
        }
      }
    }

    // Insert others' original vertices into polygon
    for (const rings of originalOtherRings) {
      for (const ring of rings) {
        for (let v = 0; v < ring.length - 1; v++) {
          clonedPolygon.geometry.coordinates = Line.insertVertex(
            ring[v],
            clonedPolygon.geometry.coordinates
          )
        }
      }
    }

    return [clonedPolygon, clonedOthers]
  }

  type RingGeometry = Coordinate[]
  type Coordinate = Position

  const splitRingsRecursive = (
    rings: RingGeometry[],
    splitter: GeoJSON.Feature<GeoJSON.LineString>
  ): RingGeometry[] => {
    const result: RingGeometry[] = []

    for (const ring of rings) {
      const cuts = Ring.findCuts(ring, splitter)

      if (cuts.length === 0) {
        // Can't split this ring, keep it as-is
        result.push(ring)
        continue
      }

      // Commit one valid cut at a time. Recursion only sees the suffix of the original
      // splitter after the accepted cut, so later segments can further subdivide
      // descendants without re-processing the already-committed interval.
      const [firstCut, ...restCuts] = cuts
      const { from, to } = firstCut

      // a single line can only split a ring into two parts, so we can safely destructure the result to two rings
      const [ring1, ring2] = Ring.split(ring, {
        first: from,
        second: to,
        connectingCut: Line.slice(from.point, to.point, splitter).geometry.coordinates
      })

      if (restCuts.length > 0) {
        const remainingSplitter = Line.slice(
          restCuts[0].from.point,
          restCuts[restCuts.length - 1].to.point,
          splitter
        )

        result.push(...splitRingsRecursive([ring1, ring2], remainingSplitter))
      } else {
        result.push(ring1, ring2)
      }
    }

    return result
  }

  /**
   * Split a polygon into multiple parts using a line
   */
  export const split = (
    polygon: GeoJSON.Feature<GeoJSON.Polygon>,
    splitter: GeoJSON.Feature<GeoJSON.LineString>
  ): GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>[] => {
    const coords = polygon.geometry.coordinates
    const exteriorRing = coords[0]
    const interiorRings = coords.slice(1)

    // Recursively split the exterior ring (starting with single ring).
    // We don't have to worry about holes at this point.
    // Since the holes themselves don't affect the splitting process, we can just subtract them later.
    const splitRings = splitRingsRecursive([exteriorRing], splitter)

    if (splitRings.length === 0) {
      throw new Error('Expected at least one ring after splitting')
    }

    if (splitRings.length === 1) {
      return [polygon]
    }

    // since the split has changed the polygons, we need to re-assign holes to them.
    // using `turf.difference` to subtract the holes from each polygon may introduce
    // some inaccuracies due to floating point precision, but it's easy and works for
    // our unit tests. A more robust solution would involve manually constructing the
    // holes for each polygon based on their spatial relationships.
    return splitRings.map((ring, index) => {
      let result: GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon> = turf.polygon([ring])

      interiorRings.forEach((hole) => {
        const diff = turf.difference(turf.featureCollection([result, turf.polygon([hole])]))
        if (isNonNil(diff)) {
          result = diff
        }
      })

      result.id = `${polygon.id}-split-${index}`
      result.properties = polygon.properties

      return result
    })
  }

  /**
   * Split a polygon wherever it narrows below `minWidthM`, so lobes joined only by a sliver come
   * apart. Each sliver goes whole to the lobe it borders most.
   */
  export const splitAtNecks = (
    polygon: GeoJSON.Feature<GeoJSON.Polygon>,
    minWidthM: number
  ): GeoJSON.Feature<GeoJSON.Polygon>[] => {
    const radius = minWidthM / 2
    const eroded = turf.buffer(polygon, -radius, { units: 'meters' })
    if (isNil(eroded)) return [polygon]
    const cores = turf.flatten(eroded).features.toSorted((a, b) => turf.area(b) - turf.area(a))
    if (cores.length < 2) return [polygon]

    // Overlap tolerance, so grown cores still meet at a neck's mouth despite the buffer's arc
    // approximation.
    const pad = Math.max(0.5, radius * 0.05)
    const lobes = cores.reduce<GeoJSON.Feature<GeoJSON.Polygon | MultiPolygon>[]>(
      (claimed, core) => {
        const grown = turf.buffer(core, radius + pad, { units: 'meters' })
        if (isNil(grown)) return claimed
        const reach = turf.intersect(turf.featureCollection([polygon, grown]))
        if (isNil(reach)) return claimed
        const lobe =
          claimed.length === 0
            ? reach
            : turf.difference(turf.featureCollection([reach, ...claimed]))
        return isNil(lobe) ? claimed : [...claimed, lobe]
      },
      []
    )
    if (lobes.length < 2) return [polygon]

    const slivers = turf.difference(turf.featureCollection([polygon, ...lobes]))
    for (const sliver of isNil(slivers) ? [] : turf.flatten(slivers).features) {
      const touching = turf.buffer(sliver, pad, { units: 'meters' })
      if (isNil(touching)) continue
      const overlap = lobes.map((lobe) => {
        const shared = turf.intersect(turf.featureCollection([touching, lobe]))
        return isNil(shared) ? 0 : turf.area(shared)
      })
      const target = overlap.indexOf(Math.max(...overlap))
      lobes[target] = turf.union(turf.featureCollection([lobes[target], sliver])) ?? lobes[target]
    }

    return lobes.flatMap((lobe) =>
      turf
        .flatten(lobe)
        // Drop sub-square-metre fragments left by boolean-op noise.
        .features.filter((part) => turf.area(part) >= 1)
        .map((part) => ({ ...part, id: crypto.randomUUID(), properties: polygon.properties }))
    )
  }

  /**
   * Determines if an exterior ring in a polygon or multipolygon feature is bisected by a given line.
   * A ring is considered bisected if the line intersects its boundary in more than one point.
   */
  export const isBisected = (feature: PolyLikeFeature, splitterLine: LineLikeFeature): boolean => {
    if (feature.geometry.type === 'MultiPolygon') {
      return turf.flatten(feature).features.some((feat) => isBisected(feat, splitterLine))
    }

    const boundary = turf.lineString(
      (feature as GeoJSON.Feature<GeoJSON.Polygon>).geometry.coordinates[0]
    )

    const intersections = turf.lineIntersect(boundary, splitterLine).features

    return intersections.length > 1 && turf.area(feature) > 0.1
  }

  /**
   * Smooths a {@link Polygon} or {@link MultiPolygon}.
   * Based on [Chaikin's algorithm](https://www.cs.unc.edu/~dm/UNC/COMP258/LECTURES/Chaikins-Algorithm.pdf).
   * Chaikin's algorithm is a simple corner cutting algorithm for smoothing out shapes
   * Warning: may create degenerate polygons.
   *
   * @function
   * @param {FeatureCollection<Polygon|MultiPolygon>|Feature<GeoPolygon | GeoMultiPolygon>|Polygon|MultiPolygon} inputPolys (Multi)Polygon(s) to smooth
   * @param {Object} [options={}] Optional parameters
   * @param {string} [options.iterations=1] The number of times to smooth the polygon. A higher value means a smoother polygon.
   * @returns {FeatureCollection<Polygon|MultiPolygon>} FeatureCollection containing the smoothed polygon/multipoylgons
   * @example
   * const polygon = turf.polygon([[[11, 0], [22, 4], [31, 0], [31, 11], [21, 15], [11, 11], [11, 0]]]);
   *
   * const smoothed = turf.polygonSmooth(polygon, {iterations: 3})
   *
   * //addToMap
   * const addToMap = [smoothed, polygon];
   */
  export const polygonSmooth: (
    inputPolys:
      | FeatureCollection<GeoPolygon | MultiPolygon>
      | GeoFeature<GeoPolygon | MultiPolygon>
      | GeoPolygon
      | MultiPolygon,
    options?: {
      iterations?: number
      boundary?: GeoFeature<GeoPolygon | MultiPolygon>
    }
  ) => FeatureCollection<GeoPolygon | MultiPolygon> = (inputPolys, options) => {
    // Optional parameters
    options = options || {}
    options.iterations = options.iterations || 1

    //chunk boundary to allow for quicker math operation than turf pointToPolygonDistance
    const boundarySegments = options.boundary ? getFeatureSegments(options.boundary) : undefined

    const { iterations } = options

    const outPolys: GeoFeature<PolyLikeGeometry>[] = []
    if (!inputPolys) throw new Error('inputPolys is required')

    turf.geomEach(inputPolys, (geom, _, properties) => {
      if (geom.type === 'Polygon') {
        let outCoords: Position[][] = [[]]
        for (let i = 0; i < iterations; i++) {
          const tempOutput: Position[][] = []
          let poly = geom.coordinates
          if (i > 0) {
            poly = outCoords
          }
          tempOutput.push(...processPolygon(poly, boundarySegments))
          outCoords = tempOutput.slice(0)
        }
        outPolys.push(turf.polygon(outCoords, properties))
      } else if (geom.type === 'MultiPolygon') {
        let outCoords: Position[][][] = [[[]]]
        for (let y = 0; y < iterations; y++) {
          const tempOutput: Position[][][] = []
          let poly = geom.coordinates
          if (y > 0) {
            poly = outCoords
          }
          tempOutput.push(...processMultiPolygon(poly, boundarySegments))
          outCoords = tempOutput.slice(0)
        }
        outPolys.push(turf.multiPolygon(outCoords, properties))
      } else {
        throw new Error('geometry is invalid, must be Polygon or MultiPolygon')
      }
    })

    return turf.featureCollection(outPolys)
  }

  const getFeatureSegments = (
    feature: GeoFeature<GeoPolygon | MultiPolygon>
  ): [Position, Position][] => {
    const segments: [Position, Position][] = []
    const geom = feature.geometry
    const coords =
      geom.type === 'Polygon' ? geom.coordinates : /*flatten MultiPolygon*/ geom.coordinates.flat()

    coords.forEach((ring) => {
      for (let i = 0; i < ring.length - 1; i++) {
        segments.push([ring[i], ring[i + 1]])
      }
    })

    return segments
  }

  function pointToSegmentDistanceSquared(p: Position, a: Position, b: Position): number {
    const dx = b[0] - a[0]
    const dy = b[1] - a[1]

    const segmentIsPoint = dx === 0 && dy === 0
    if (segmentIsPoint) {
      return Math.hypot(p[0] - a[0], p[1] - a[1])
    }

    //dot product projection test if a perpendicular line from point p is between a and b
    // (0<t<1 === between the points, t=0 perpendicular passes through a, t=1 passes through b )
    const t = ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / (dx * dx + dy * dy)

    const closestPointOnSegment = Math.max(0, Math.min(1, t))
    const projX = a[0] + closestPointOnSegment * dx
    const projY = a[1] + closestPointOnSegment * dy

    //get distance to closest point
    return (p[0] - projX) * (p[0] - projX) + (p[1] - projY) * (p[1] - projY)
  }

  function isCloseToBoundary(p: Position, segments: [Position, Position][], tolerance: number) {
    const toleranceSquared = tolerance * tolerance
    let min = Infinity
    for (const seg of segments) {
      const d = pointToSegmentDistanceSquared(p, seg[0], seg[1])
      if (d < min) min = d
      if (min <= toleranceSquared) {
        return true
      }
    }
    return false
  }

  const buildBoundaryMask = (
    ring: Position[],
    boundarySegments: [Position, Position][],
    thresholdDegrees = 0.00000225 //degrees to avoid any unnecessary calculations on points
  ): boolean[] => {
    return Array.from({ length: ring.length - 1 }, (_, i) =>
      isCloseToBoundary(ring[i], boundarySegments, thresholdDegrees)
    )
  }

  //Based on [Chaikin's algorithm](https://www.cs.unc.edu/~dm/UNC/COMP258/LECTURES/Chaikins-Algorithm.pdf).
  // Chaikin's algorithm is a simple corner cutting algorithm for smoothing out shapes
  //it essentially takes the points at 1/4 the distance between 2 points and 3/4 the distance
  //this creates a smoothed a
  const chaikinRing = (ring: Position[], out: Position[], preventSmoothingAtPoint?: boolean[]) => {
    const n = ring.length - 1 // closed ring assumed

    for (let i = 0; i < n; i++) {
      const p0 = ring[i]
      const p1 = ring[i + 1]

      if (preventSmoothingAtPoint?.[i] === true) {
        out.push(p0)
        continue
      }

      const qx = 0.75 * p0[0] + 0.25 * p1[0]
      const qy = 0.75 * p0[1] + 0.25 * p1[1]
      const rx = 0.25 * p0[0] + 0.75 * p1[0]
      const ry = 0.25 * p0[1] + 0.75 * p1[1]

      out.push([qx, qy], [rx, ry])
    }

    // Close ring safely
    if (out.length > 0) {
      out.push(out[0])
    }
  }

  /**
   * @param {poly} poly to process
   * @param {poly} tempOutput to place the results in
   * @private
   */
  const processPolygon = (polyCoords: Position[][], boundary?: [Position, Position][]) => {
    const tempOutput: Position[][] = []
    polyCoords.forEach((ring, index) => {
      const outRing: Position[] = []

      //only apply boundary to outer ring
      const preventSmoothingAtPoint =
        boundary && index === 0 ? buildBoundaryMask(ring, boundary) : undefined

      chaikinRing(ring, outRing, preventSmoothingAtPoint)
      tempOutput.push(outRing)
    })
    return tempOutput
  }
  /**
   * @param {GeoMultiPolygon} poly
   * @param {Position[][][]} tempOutput
   * @private
   */
  const processMultiPolygon = (polyCoords: Position[][][], boundary?: [Position, Position][]) => {
    const tempOutput: Position[][][] = []

    polyCoords.forEach((polygonCoords) => {
      const polygonOutput: Position[][] = []

      polygonOutput.push(...processPolygon(polygonCoords, boundary))

      tempOutput.push(polygonOutput)
    })
    return tempOutput
  }
}
